import { useContext, useEffect, useState } from "react";
import Input from "../../../../components/Inputs/Input";
import coloredWarningImage from "../../../../assets/Icons/colored-warning.svg";
import { createPart } from "../../services/PartsConfigurationService";
import { Dropdowns } from "../../../../components/Dropdowns/Dropdowns";
import CheckboxField from "../../../../components/Checkbox/CheckboxField";
import MultioptionButton from "../../../../components/Buttons/MultioptionButton";
import { useSnackBar } from "../../../../hooks/useSnackBar";
import { DropdownsContext } from "../../../../contexts/DropdownsContext";
import { usePermissions } from "../../../../hooks/usePermissions";
import {
  localStorageGet,
  localStorageSave,
} from "../../../../services/localStorageService";
import { TypeSeriesConfigurationContext } from "../../contexts/TypeSeriesConfigurationContext";
import {
  createType,
  getTypeSeriesConfigurationInfo,
} from "../../services/AircraftConfigurationService";
import { HTTP_RESPONSE_CODES } from "../../../../globals/globals";

// const dropdownIds = [4, 5, 6, 7];
const dropdownNames = { 30: "type", 31: "series", 7: "manufacturerId" };

const defaultEmptyValues = {
  type: null,
  series: null,
  manufacturerId: null,
  basicFlightTime: "",
  averageFlightTime: "",
  numberOfEngine: "",
  numberOfLandingGears: "",
  etopsHours: "",
  sgmlDesignator: "",
  ronHours: "",
  scheduleExtensionLimit: "",
  scheduleExtensionMaxHours: "",
  scheduleExtensionMaxCycles: "",
  scheduleExtensionMaxDays: "",
  totalAircraft: {
    active: 0,
    inactive: 0,
    hold: 0,
  },
  countChilds: 0,
};
const hideFieldsWhenDisabled = new Set(["Type", "Series", "Manufacturer"]);
const localStoragePrefix = "configuration/typeSeries/draft/general";

const GeneralTypeSeriesForm = ({
  onChange,
  open,
  aircraft,
  disabled,
  setOpen,
}) => {
  const {
    dropdownsData,
    setDropdownsData,
    setDropdownIds,
    dropdownDefaultOptions,
  } = useContext(DropdownsContext);
  const [values, setValues] = useState({ ...defaultEmptyValues });
  const [backup, setBackup] = useState(null);
  const [defaults, setDefaults] = useState({});
  const { hasPermission } = usePermissions();
  const [partBackup, setPartBackup] = useState(null);
  const [focusDropdown, setFocusDropdown] = useState(false);
  // const [defaults, setDefaults] = useState(null);

  useEffect(() => {
    if (!disabled && typeof values.id === "number") {
      localStorageSave(localStoragePrefix, { id: aircraft.id }, values);
    }
  }, [values]);

  const {
    saveDraft,
    removeDraft,
    updateValue,
    resetType,
    confirmType,
    newDrafts,
  } = useContext(TypeSeriesConfigurationContext);
  const { successMsg, warningMsg, infoMsg } = useSnackBar();
  const cancelEdit = () => {
    removeDraft(aircraft.id, "general");
    setValues(backup ?? defaultEmptyValues);
    setDefaults(backup ?? defaultEmptyValues);
    resetType(aircraft.id);
    setOpen(false);
  };
  const disabledSeries = () => {
    if (values.type != null) {
      return false;
    }
    return true;
  };
  useEffect(() => {
    if (open) {
      getTypeSeriesConfigurationInfo(aircraft.id)
        .then((response) => {
          if (response.data.responseCode === 200) {
            if (response.data.load != null) {
              let {
                id,
                series,
                basicFlightTime,
                averageFlightTime,
                etopsHours,
                ronHours,
                sgmlDesignator,
                scheduleExtensionLimit,
                scheduleExtensionMaxHours,
                scheduleExtensionMaxCycles,
                scheduleExtensionMaxDays,
                version,
              } = response.data.load;
              setPartBackup(response.data.load);
              // let draftValues = JSON.parse(localStorage.getItem(`${localStoragePrefix}?id=${part.id}`) ?? "{}");
              let draftValues = localStorageGet(localStoragePrefix, {
                id: aircraft.id.toString(),
              })[0];
              let form = {
                ...defaultEmptyValues,
                id,
                type: series?.typeId.id,
                series: series?.id,
                manufacturerId: series?.typeId?.manufacturerId,
                basicFlightTime: basicFlightTime,
                averageFlightTime: averageFlightTime,
                numberOfEngine: series?.typeId?.numberOfEngines,
                numberOfLandingGears: series?.typeId?.numberOfLandingGears,
                etopsHours: etopsHours,
                sgmlDesignator: sgmlDesignator,
                ronHours: ronHours,
                scheduleExtensionLimit: scheduleExtensionLimit,
                scheduleExtensionMaxHours: scheduleExtensionMaxHours,
                scheduleExtensionMaxCycles: scheduleExtensionMaxCycles,
                scheduleExtensionMaxDays: scheduleExtensionMaxDays,
                version: version,
                totalAircraft: {
                  active: 0,
                  inactive: 0,
                  hold: 0,
                },
                isAircraft: true,
                countChilds: 0,
              };
              setDefaults({ ...form, ...draftValues });
              setValues({ ...form, ...draftValues });
              setBackup(form);
            }
          }
        })
        .catch((error) => {
          console.error(error);
          let form = { ...defaultEmptyValues };
          setDefaults(form);
          setValues(form);
        });
    }
  }, [open, aircraft]);

  const generalSectionButtons = [
    {
      label: "Save and Finish",
      onClick: () => saveSection(true),
      icon: "icon-save",
      id: "save",
    },
    {
      label: "Save and Continue Editing",
      onClick: () => saveSection(false),
      icon: "icon-save",
      id: "save-continue",
    },
    {
      label: "Cancel",
      onClick: () => cancelEdit(),
      icon: "icon-cross",
      id: "cancel",
      type: "danger",
    },
  ];

  const changeValue = (e) => {
    const { name, value, completeValue } = e.target;
    setValues((prev) => {
      onChange?.(
        { name, value: completeValue ?? value },
        { ...prev, [name]: value }
      );
      return {
        ...prev,
        ...(fieldChangeMapper[name]?.(value) ?? { [name]: value }),
      };
    });
    updateValue(aircraft.id, name, completeValue);
  };
  const fieldChangeMapper = {
    type: (value) => ({ type: value, series: null }),
  };
  const saveSection = (disableForm) => {
    infoMsg("Saving...");
    // let draft = JSON.parse(localStorage.getItem(`${localStoragePrefix}?id=${part.id}`) ?? "{}");
    let draft = localStorageGet(localStoragePrefix, {
      id: aircraft.id.toString(),
    })[0];

    draft.series = { id: draft.series };
    draft.scheduleExtensionLimit = Number(draft.scheduleExtensionLimit);
    draft.scheduleExtensionMaxCycles = Number(draft.scheduleExtensionMaxCycles);
    draft.scheduleExtensionMaxDays = Number(draft.scheduleExtensionMaxDays);
    draft.scheduleExtensionMaxHours = Number(draft.scheduleExtensionMaxHours);
    createType(draft)
      .then((response) => {
        if (response.data.responseCode === HTTP_RESPONSE_CODES.UPDATED) {
          successMsg("Type/Series saved successfully");
          let {
            id,
            series,
            basicFlightTime,
            averageFlightTime,
            etopsHours,
            ronHours,
            sgmlDesignator,
            scheduleExtensionLimit,
            scheduleExtensionMaxHours,
            scheduleExtensionMaxCycles,
            scheduleExtensionMaxDays,
            version,
          } = response.data.load;

          let form = {
            id,
            type: series?.typeId.id,
            series: series?.id,
            manufacturerId: series?.typeId?.manufacturerId,
            basicFlightTime: basicFlightTime,
            averageFlightTime: averageFlightTime,
            numberOfEngine: series?.typeId?.numberOfEngines,
            numberOfLandingGears: series?.typeId?.numberOfLandingGears,
            etopsHours: etopsHours,
            sgmlDesignator: sgmlDesignator,
            ronHours: ronHours,
            scheduleExtensionLimit: scheduleExtensionLimit,
            scheduleExtensionMaxHours: scheduleExtensionMaxHours,
            scheduleExtensionMaxCycles: scheduleExtensionMaxCycles,
            scheduleExtensionMaxDays: scheduleExtensionMaxDays,
            version: version,
            isAircraft: true,
          };
          setValues({ ...defaultEmptyValues, ...form });
          setBackup({ ...defaultEmptyValues, ...form });
          confirmType(aircraft.id);
          if (disableForm) {
            removeDraft(aircraft.id, "general");
            // setOpen(false);
          }
        } else if (
          response.data.responseCode === HTTP_RESPONSE_CODES.VALIDATIONS_FAILED
        ) {
          warningMsg(
            response.data.message ||
              `Something went wrong: ${response.data.responseCode}`,
            "One or more fields are invalid",
            30000
          );
        } else {
          warningMsg(
            response.data.message ||
              `Something went wrong: ${response.data.responseCode}`,
            30000
          );
        }
      })
      .catch((error) => warningMsg(`Something went wrong: ${error}`));
  };
  useEffect(() => {
    if (open == false) {
      setFocusDropdown(false);
    }
  }, [open]);
  const handleDropdownValue = (value, name, data) => {
    changeValue({ target: { name, value: value[data], completeValue: value } });
    let form = {};
    if (name == "type") {
      form = {
        type: value[data],
        numberOfEngine: value.number_of_engines,
        numberOfLandingGears: value.number_of_landing_gears,
        manufacturerId: value.manufacturer_id,
      };
      setValues((prevValues) => ({
        ...prevValues,
        ...form,
      }));
    }
    if (name == "series") {
      form = {
        series: value[data],
        basicFlightTime: value.bft,
        averageFlightTime: value.aft,
        etopsHours: value.etops_hours,
        sgmlDesignator: value["acs.sgml_designator"],
        ronHours: value.ron_hours,
        scheduleExtensionLimit: value.schedule_extension_limit,
        scheduleExtensionMaxHours: value.schedule_extension_max_hours,
        scheduleExtensionMaxCycles: value.schedule_extension_max_cycles,
        scheduleExtensionMaxDays: value.schedule_extension_max_days,
      };
      setValues((prevValues) => ({
        ...prevValues,
        ...form,
      }));
    }
    setDefaults((prevValues) => ({
      ...prevValues,
      ...form,
      ...(fieldChangeMapper[name]?.(value[data]) ?? { [name]: " " }),
    }));
  };
  useEffect(() => {
    let isNewDraft = aircraft.id in newDrafts;
    if (isNewDraft) {
      const draft = newDrafts[aircraft.id];
      if (draft.type == null) {
        setFocusDropdown(true);
      }
    }
  }, []);

  const updateDropdown = (dropdownId, parentValues, dependenciesToUpdate) => {
    setDropdownsData((prevDropdowns) =>
      prevDropdowns.map((filter) => {
        if (filter.id === dropdownId) {
          return {
            ...filter,
            parentValues,
            dependenciesToUpdate,
            reload: true,
          };
        }
        return filter;
      })
    );
  };
  const isAirbus = false;
  const resetDropdownsReload = () => {
    setDropdownsData((filterState) => {
      if (filterState.length > 0) {
        return filterState.map((e) => {
          if (e.reload) {
            return { ...e, reload: false };
          }
          return e;
        });
      }
      return filterState;
    });
  };

  return (
    open && (
      <div className="general-aircraft-section-form-wrapper">
        <div className="general-section-aircraft-form-component">
          <div className="general-section-aircraft-form-header">
            <div className="title-container">
              <b>General Information</b>
            </div>
            <div className="general-info-actions">
              {disabled && hasPermission("update") && (
                <button
                  className="outlined iconTextButton small-size"
                  onClick={() => saveDraft(aircraft.id, "general", values)}
                >
                  <i className="icon-edit"></i>
                  <span>Edit</span>
                </button>
              )}
              {!disabled && hasPermission("update") && (
                <MultioptionButton buttons={generalSectionButtons} />
              )}
            </div>
          </div>
          <div className="general-aircraft-form">
            <Dropdowns
              context={{
                dropdownsData,
                setDropdownsData,
                setDropdownIds,
                dropdownDefaultOptions,
              }}
              updateDropdown={updateDropdown}
              resetDropdownsReload={resetDropdownsReload}
              hideWhenDisabled={hideFieldsWhenDisabled.has(dropdownNames[30])}
              disabled={disabled}
              required={true}
              key={30}
              onOptionSelect={(value, data) =>
                handleDropdownValue(value, dropdownNames[30], data)
              }
              showPagination={false}
              data={dropdownsData.find((e) => e.id === 30)}
              preSelectValue={defaults?.[dropdownNames[30]]}
              externalOpening={focusDropdown}
              parentComponentId={aircraft.id}
            />
            <Dropdowns
              context={{
                dropdownsData,
                setDropdownsData,
                setDropdownIds,
                dropdownDefaultOptions,
              }}
              updateDropdown={updateDropdown}
              resetDropdownsReload={resetDropdownsReload}
              hideWhenDisabled={hideFieldsWhenDisabled.has(dropdownNames[7])}
              disabled={true}
              key={7}
              required={true}
              onOptionSelect={(value) =>
                handleDropdownValue(value, dropdownNames[7])
              }
              showPagination={false}
              data={dropdownsData.find((e) => e.id === 7)}
              preSelectValue={defaults?.[dropdownNames[7]]}
            />
            <div className="general-aircraft-form-row">
              <Input
                style={{ gridColumn: "span 1" }}
                disabled={true}
                onChange={changeValue}
                required={true}
                name="numberOfLandingGears"
                label={"Number of Landing Gears"}
                value={values["numberOfLandingGears"]}
              />
              <Input
                style={{ gridColumn: "span 1" }}
                disabled={true}
                onChange={changeValue}
                required={true}
                name="numberOfEngine"
                label={"Number of Engines"}
                value={values["numberOfEngine"]}
              />
            </div>
          </div>
          <div className="general-aircraft-form">
            <Dropdowns
              context={{
                dropdownsData,
                setDropdownsData,
                setDropdownIds,
                dropdownDefaultOptions,
              }}
              updateDropdown={updateDropdown}
              resetDropdownsReload={resetDropdownsReload}
              hideWhenDisabled={hideFieldsWhenDisabled.has(dropdownNames[31])}
              disabled={disabled || disabledSeries()}
              key={31}
              required={true}
              onOptionSelect={(value, data) =>
                handleDropdownValue(value, dropdownNames[31], data)
              }
              showPagination={false}
              data={dropdownsData.find((e) => e.id === 31)}
              preSelectValue={defaults?.[dropdownNames[31]]}
              parentComponentId={aircraft.id}
            />
            <div className="general-aircraft-form-row">
              <Input
                style={{ gridColumn: "span 1" }}
                disabled={disabled || disabledSeries()}
                onChange={changeValue}
                name="etopsHours"
                label={"ETOPS Default"}
                value={values["etopsHours"]}
              />
              <Input
                style={{ gridColumn: "span 1" }}
                disabled={disabled || disabledSeries()}
                name="sgmlDesignator"
                onChange={changeValue}
                label={"SGML Designator"}
                value={values["sgmlDesignator"]}
              />
            </div>
            <Input
              style={{ gridColumn: "span 1" }}
              disabled={disabled || disabledSeries()}
              name="ronHours"
              onChange={changeValue}
              label={"RON Hours"}
              value={values["ronHours"]}
            />
          </div>

          <div className="general-aircraft-form">
            <Input
              style={{ gridColumn: "span 1" }}
              onChange={changeValue}
              disabled={disabled || disabledSeries()}
              isNumeric={true}
              name="scheduleExtensionLimit"
              limit={100}
              label={"Schedule extension Limit 0-100 (%)"}
              value={values["scheduleExtensionLimit"]}
            />
            <div
              className="general-aircraft-form-row"
              style={{ gridColumn: "span 2" }}
            >
              <span className="info">Schedule extension Limit Max </span>
              <Input
                style={{ gridColumn: "span 1" }}
                disabled={disabled || disabledSeries()}
                onChange={changeValue}
                isNumeric={true}
                name="scheduleExtensionMaxHours"
                label={"Hours"}
                value={values["scheduleExtensionMaxHours"]}
              />
              <Input
                style={{ gridColumn: "span 1" }}
                disabled={disabled || disabledSeries()}
                isNumeric={true}
                name="scheduleExtensionMaxCycles"
                onChange={changeValue}
                label={"Cycles"}
                value={values["scheduleExtensionMaxCycles"]}
              />
              <Input
                style={{ gridColumn: "span 1" }}
                disabled={disabled || disabledSeries()}
                onChange={changeValue}
                isNumeric={true}
                name="scheduleExtensionMaxDays"
                label={"Days"}
                value={values["scheduleExtensionMaxDays"]}
              />
            </div>
          </div>
          {isAirbus && (
            <div className="general-aircraft-form">
              <Input
                style={{ gridColumn: "span 1" }}
                disabled={disabled || disabledSeries()}
                name="basicFlightTime"
                onChange={changeValue}
                label={"BFT (Airbus) Basic Flight Time"}
                value={values["basicFlightTime"]}
              />
              <Input
                style={{ gridColumn: "span 1" }}
                disabled={disabled || disabledSeries()}
                name="averageFlightTime"
                onChange={changeValue}
                label={"AFT (Airbus) Average Flight Time"}
                value={values["averageFlightTime"]}
              />
            </div>
          )}
        </div>
      </div>
    )
  );
};

export default GeneralTypeSeriesForm;
